export * from './use-session-filters'
import React, { useState, useEffect, ReactNode } from 'react'
import {
  shouldClearSessionFilters,
  useSessionFilters
} from './use-session-filters'
import { FilterType } from '../filter-manager'

export interface RouteContextValue {
  parameters: string[]
  queryParameters: Map<string, string[]>
  getQueryParamsAsString: () => string
  setQueryParameter: (
    filterType: FilterType,
    parameter: string,
    value: string | string[]
  ) => void
  setQueryParameters: (
    filterType: FilterType,
    parameterValueMap: Map<string, string[] | string | null>
  ) => void
  toggleQueryParameters: (
    filterType: FilterType,
    parameterValueMap: Map<string, string>
  ) => void
  toggleQueryParameter: (
    filterType: FilterType,
    parameter: string,
    value: string
  ) => void
  clear: (filterType: FilterType, parameters: string[] | string) => void
  clearAll: (filterType: FilterType) => void
  applyExisitingFilters: (filterType: FilterType) => void
  updateQueryParameters: (
    filterType: FilterType,
    newParameters: Map<string, string[]>
  ) => void
  isCurrentQueryParam: (parameter: string, value: string) => boolean
  organizationID?: string
  convertQueryParamMapToString: (queryMap: Map<string, string[]>) => string
  addSessionFilters: (
    filterType: FilterType,
    filters: Map<string, string[]>
  ) => void
  removeSessionFilters: (
    filterType: FilterType,
    filters: Map<string, string[]>
  ) => void
  loadSessionFilters: (
    filterType: FilterType,
    filterTypeTarget?: FilterType
  ) => void
  setIsMostRecentSession: (flag: boolean) => void
  sessionFilters: Map<string, Map<string, string[] | null>>
  isMostRecentSession: boolean
}

interface Document {
  addEventListener: (event: string, callback: () => void) => void
  removeEventListener: (event: string, callback: () => void) => void
  location: {
    hash: string
    replace: (url: string) => void
  }
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export const routeContextValues = {
  parameters: [],
  queryParameters: new Map<string, string[]>(),
  getQueryParamsAsString: () => '',
  setQueryParameter: noop,
  setQueryParameters: noop,
  toggleQueryParameters: noop,
  toggleQueryParameter: noop,
  clear: noop,
  clearAll: noop,
  applyExisitingFilters: noop,
  updateQueryParameters: noop,
  convertQueryParamMapToString: (_queryMap: Map<string, string[]>) => '',
  isCurrentQueryParam: () => false,
  organizationID: undefined,
  addSessionFilters: noop,
  removeSessionFilters: noop,
  loadSessionFilters: noop,
  setIsMostRecentSession: noop,
  sessionFilters: new Map<string, Map<string, string[] | null>>(),
  isMostRecentSession: false
}

export const RouteContext =
  React.createContext<RouteContextValue>(routeContextValues)

interface Props {
  environment?: Document
  children?: ReactNode
}

export function RouteProvider({ environment = window, children }: Props) {
  // In order to keep state management from being "backwards", please call "updateHash()" to keep the
  // React hash state variable and the DOM's environment.location.hash in sync with each other.  Thx!
  const [hash, setHash] = useState<string>(environment.location.hash) // please see above comment!

  const [filters, setFilters] = useState(
    new Map<string, Map<string, string[]>>()
  )

  // Sets multiple params of the state variable, hash, in one click event.
  // Updating in one event will keep the state update synchronous and hash will be in sync with environment.location.hash.
  const setQueryParameters = (
    filterType: FilterType,
    parameterValueMap: Map<string, string[] | string | null>
  ) => {
    const currentFilters = getCurrentQueryParams()

    for (const [param, value] of parameterValueMap) {
      if (typeof value === 'string') {
        currentFilters.set(param, [value])
      } else if (value === null) {
        currentFilters.delete(param)
      } else if (typeof value === 'object') {
        currentFilters.set(param, value)
      }
    }
    updateQueryStringInURL(
      filterType,
      environment.location.hash,
      currentFilters
    )
  }

  // setQueryParameters brought to top for use with session filters
  const {
    addSessionFilters,
    removeSessionFilters,
    loadSessionFilters,
    setIsMostRecentSession,
    sessionFilters,
    isMostRecentSession,
    clearSessionFilters
  } = useSessionFilters({ setQueryParameters })

  useEffect(() => {
    const onHashChange = () => {
      const shallClearSessionFilters = () => {
        return shouldClearSessionFilters(hash, environment.location.hash)
      }
      if (shallClearSessionFilters()) {
        clearSessionFilters()
      }

      setHash(environment.location.hash)
    }

    environment.addEventListener('hashchange', onHashChange)

    return function cleanup() {
      environment.removeEventListener('hashchange', onHashChange)
    }
  }, [environment, environment.location.hash, clearSessionFilters, hash])

  const updateHash = (aHash: string) => {
    setHash(aHash)
    if (environment?.location?.hash !== aHash) {
      environment.location.hash = aHash
    }
  }

  const clearAll = (filterType: FilterType) => {
    updateQueryStringInURL(
      filterType,
      environment.location.hash,
      new Map<string, string[]>()
    )
  }

  const updateQueryParameters = (
    filterType: FilterType,
    newFilters: Map<string, string[]>
  ) => {
    const filterCopy = filters
    filterCopy.set(filterType, newFilters)
    setFilters(filterCopy)
  }

  const applyExisitingFilters = (filterType: FilterType) => {
    const currentFilters = filters.get(filterType)

    const queryParamString = convertQueryParamMapToString(
      currentFilters ? currentFilters : new Map<string, string[]>()
    )
    const hashWithoutQuery = hash.split('?')[0]
    let newHash = `${hashWithoutQuery}`
    if (queryParamString) {
      newHash = `${newHash}?${queryParamString}`
    }

    updateHash(newHash)
  }

  const clear = (filterType: FilterType, parameters: string[] | string) => {
    const currentFilters = getCurrentQueryParams()
    if (typeof parameters === 'string') {
      currentFilters.set(parameters, [])
    }
    if (typeof parameters === 'object') {
      parameters.forEach((parameter) => {
        currentFilters.set(parameter, [])
      })
    }
    updateQueryStringInURL(
      filterType,
      environment.location.hash,
      currentFilters
    )
  }

  const setQueryParameter = (
    filterType: FilterType,
    parameter: string,
    value: string | string[]
  ) => {
    const currentFilters = getCurrentQueryParams()

    if (typeof value === 'string') {
      currentFilters.set(parameter, [value])
      updateQueryStringInURL(
        filterType,
        environment.location.hash,
        currentFilters
      )
    }
    if (typeof value === 'object') {
      currentFilters.set(parameter, value)
      updateQueryStringInURL(
        filterType,
        environment.location.hash,
        currentFilters
      )
    }
  }

  // Allows multiple params to be added before triggering a rerender.
  const toggleQueryParameters = (
    filterType: FilterType,
    parameterValueMap: Map<string, string>
  ) => {
    const currentFilters = getCurrentQueryParams()

    parameterValueMap.forEach((value, param) =>
      toggleParameter(currentFilters, param, value)
    )

    updateQueryStringInURL(
      filterType,
      environment.location.hash,
      currentFilters
    )
  }

  const toggleQueryParameter = (
    filterType: FilterType,
    parameter: string,
    value: string
  ) => {
    const newFilters = toggleParameter(
      getCurrentQueryParams(),
      parameter,
      value
    )

    updateQueryStringInURL(filterType, environment.location.hash, newFilters)
  }

  function updateQueryStringInURL(
    filterType: FilterType,
    aHash: string,
    newFilters: Map<string, string[]>
  ) {
    const queryParamString = convertQueryParamMapToString(newFilters)
    const hashWithoutQuery = aHash.split('?')[0]
    const filtersCopy = filters

    filtersCopy.set(filterType, newFilters)
    setFilters(filtersCopy)

    let newHash = `${hashWithoutQuery}`
    if (queryParamString) {
      newHash = `${newHash}?${queryParamString}`
    }

    // Setting the environment.location.hash instead of calling "updateHash()" is being retained here for
    // backwards compatability with the session filter feature.
    environment.location.hash = newHash
  }

  function isCurrentQueryParam(parameter: string, value: string) {
    const currentFilters = getCurrentQueryParams()
    const urlValues = currentFilters.get(parameter)

    return urlValues ? urlValues.includes(value) : false
  }

  function getCurrentQueryParams(): Map<string, string[]> {
    const queryParamMap: Map<string, string[]> = new Map<string, string[]>()

    const currentRoute = environment.location.hash
    if (currentRoute.indexOf('?') < 0) {
      return queryParamMap
    }

    currentRoute
      .substring(currentRoute.indexOf('?') + 1)
      .split('&')
      .forEach((queryItem) => {
        const items = queryItem.split('=')

        const current =
          queryParamMap.get(decodeURIComponent(items[0])) !== undefined
            ? queryParamMap.get(decodeURIComponent(items[0]))
            : []
        if (current) {
          current.push(decodeURIComponent(items[1]))
          queryParamMap.set(decodeURIComponent(items[0]), current)
        }
      })

    return queryParamMap
  }

  function getParameters(): string[] {
    if (hash.indexOf('?') < 0) {
      return hash.split('/').slice(1)
    }

    return hash.substring(0, hash.indexOf('?')).split('/').slice(1)
  }

  function getOrganizationID(): string | undefined {
    const parameters = getParameters()

    return isFreemiumPortal(parameters) ||
      isSuperAdminPortal(parameters) ||
      isAuxiliaryPage(parameters)
      ? undefined
      : parameters[0]
  }

  function isFreemiumPortal(parameters: string[]): boolean {
    return parameters[0] === 'freemium'
  }

  function isSuperAdminPortal(parameters: string[]): boolean {
    return parameters[0] === 'superadmin'
  }

  function isAuxiliaryPage(parameters: string[]): boolean {
    return parameters[0] === 'pages'
  }

  return (
    <RouteContext.Provider
      value={{
        parameters: getParameters(),
        queryParameters: getCurrentQueryParams(),
        getQueryParamsAsString: () =>
          getQueryParamsAsString(environment.location.hash),
        convertQueryParamMapToString,
        setQueryParameter,
        setQueryParameters,
        toggleQueryParameters,
        toggleQueryParameter,
        clear,
        clearAll,
        applyExisitingFilters,
        updateQueryParameters,
        isCurrentQueryParam,
        addSessionFilters,
        removeSessionFilters,
        loadSessionFilters,
        setIsMostRecentSession,
        sessionFilters,
        isMostRecentSession,
        organizationID: getOrganizationID()
      }}
    >
      {children}
    </RouteContext.Provider>
  )
}

export function getQueryParamsAsString(currentHash: string) {
  if (currentHash.indexOf('?') < 0) {
    return ''
  }
  return currentHash.split('?')[1]
}

export function convertQueryParamMapToString(
  queryParamMap: Map<string, string[] | string>
): string {
  let queryParamString = ''
  queryParamMap.forEach((value, key) => {
    const valueArray: string[] = Array.isArray(value) ? value : [value]

    valueArray.forEach((item) => {
      if (queryParamString !== '') {
        queryParamString = queryParamString + '&'
      }
      queryParamString =
        queryParamString +
        (encodeURIComponent(key) + '=' + encodeURIComponent(item))
    })
  })
  return queryParamString
}

export function convertQueryParamStringToMap(
  queryParamString: string
): Map<string, string> {
  const queryParamMap: Map<string, string> = new Map<string, string>()
  if (!queryParamString) {
    return queryParamMap
  }
  queryParamString.split('&').forEach((queryItem) => {
    const items = queryItem.split('=')
    const current =
      queryParamMap.get(decodeURIComponent(items[0])) !== undefined
        ? [queryParamMap.get(decodeURIComponent(items[0]))]
        : []
    if (current) {
      current.push(decodeURIComponent(items[1]))

      queryParamMap.set(decodeURIComponent(items[0]), current.toString())
    }
  })
  return queryParamMap
}

const toggleParameter = (
  paramValueMap: Map<string, string[]>,
  parameter: string,
  value: string
) => {
  const currentValues = paramValueMap.get(parameter)

  if (currentValues && currentValues.includes(value)) {
    removeParameter(paramValueMap, parameter, value)
  } else {
    addParameter(paramValueMap, parameter, value)
  }

  return paramValueMap
}

const removeParameter = (
  paramValueMap: Map<string, string[]>,
  parameter: string,
  value: string
) => {
  const currentValues = paramValueMap.get(parameter)
  if (currentValues && currentValues.includes(value)) {
    const newValues = currentValues.filter((item) => {
      return item !== value
    })
    paramValueMap.set(parameter, newValues)
  }
}

const addParameter = (
  paramValueMap: Map<string, string[]>,
  parameter: string,
  value: string
) => {
  const currentValues = paramValueMap.get(parameter)
    ? paramValueMap.get(parameter)
    : []
  if (currentValues && !currentValues.includes(value)) {
    currentValues.push(value)
    paramValueMap.set(parameter, currentValues)
  }
}
